import {
  BulkRawMaterialDto,
  CharInstrumentDropdownChoicesDto,
  CollaboratorDto,
  InstrumentFundingDto,
  MaterialDto,
  ProjectDto,
  ProjectTypeDto,
  ReportingCategoryDto,
  RoleDto,
  SampleDropdownChoicesDto,
  SubstrateDto,
  SubstrateDropdownChoicesDto,
  CharacterizationInstrumentDto,
  CharacterizationTechniqueDto,
  SynthesisInstrumentDto,
  SynthesisTechniqueDto,
  ScienceDriverDto,
} from '@/api/ApiClient';

/**
 * Available types. Those are both actual entities, as well as key/value pairs for select lists.
 * We have to add some more key/value pairs here for stuff like instruments,
 * so we can avoid doing that in all components showing such entities in a select.
 */
export type OptionKey =
  /// actual entities
  | 'staff'
  | 'users'
  | 'roles'
  | 'userRoles'
  | 'synthesisInstruments'
  | 'synthesisTechniques'
  | 'synthesisTechniquesLeafOnly'
  | 'synthesisInstrumentTypes'
  | 'characterizationInstruments'
  | 'characterizationTechniques'
  | 'materials'
  | 'projectMaterials'
  | 'sampleMaterials'
  | 'substrates'
  | 'substrateDropdownChoices'
  | 'bulkRawMaterials' // LEGACY: For recipe lookup columns, should be removed eventually
  | 'sampleDropdownChoices'
  | 'characterizationDropdownChoices'
  | 'projects'
  | 'scienceDrivers'
  /// derived keys
  | 'facultyOptions'
  | 'researcherOptions'
  | 'researcherFacultyOptions'
  | 'researchGroupMap'
  | 'externalUserOptions'
  | 'synthesisInstrumentOptions'
  | 'characterizationInstrumentOptions'
  | 'sampleMaterialOptions'
  /// select lists
  | 'columnType'
  | 'metadataSource'
  | 'instrumentFunding'
  | 'projectCategory'
  | 'projectSearchStatus'
  | 'projectType'
  | 'publicationType'
  | 'projectStatus'
  | 'sampleReportingCategory'
  | 'status'
  | 'visibility'
  | 'activityCategory'
  | 'sampleRequestStatus'
  | 'activityType'
  | 'reportObject'
  | 'reportAggregation'
  | 'reportAttribute'
  | 'shippingLabelTypes'
  | 'shippingTypes';

/**
 * This is a map of keys to the keys they derived from. We need to load the latter from the backend,
 * and then create the former.
 */
export const derivedKeys: Map<OptionKey, OptionKey[]> = new Map([
  ['researcherOptions', ['staff']],
  ['facultyOptions', ['staff']],
  ['researcherFacultyOptions', ['staff']],
  ['researchGroupMap', ['staff']],
  ['externalUserOptions', ['users']],
  ['userRoles', ['roles']],
  ['synthesisInstrumentOptions', ['synthesisInstruments']],
  ['characterizationInstrumentOptions', ['characterizationInstruments']],
  ['synthesisTechniquesLeafOnly', ['synthesisTechniques']],
  ['sampleMaterialOptions', ['sampleMaterials']],
]);

export type KeyValueType = {
  key: string;
  value: string;
  /** Optional parent/scope key from dropdowns (e.g. reportAttribute targets). */
  masterKey?: string;
};

export type OptionMap = {
  staff: CollaboratorDto[];
  users: CollaboratorDto[];
  roles: RoleDto[];
  userRoles: RoleDto[];
  synthesisInstruments: SynthesisInstrumentDto[];
  synthesisTechniques: SynthesisTechniqueDto[];
  synthesisTechniquesLeafOnly: SynthesisTechniqueDto[];
  synthesisInstrumentTypes: KeyValueType[];
  characterizationInstruments: CharacterizationInstrumentDto[];
  characterizationTechniques: CharacterizationTechniqueDto[];
  characterizationDropdownChoices: CharInstrumentDropdownChoicesDto;
  materials: MaterialDto[];
  projectMaterials: MaterialDto[];
  sampleMaterials: MaterialDto[];
  substrates: SubstrateDto[];
  substrateDropdownChoices: SubstrateDropdownChoicesDto;
  bulkRawMaterials: BulkRawMaterialDto[]; // LEGACY: For recipe lookup columns
  projects: ProjectDto[];
  scienceDrivers: ScienceDriverDto[];
  sampleDropdownChoices: SampleDropdownChoicesDto;
  researcherOptions: KeyValueType[];
  facultyOptions: KeyValueType[];
  researcherFacultyOptions: KeyValueType[];
  externalUserOptions: KeyValueType[];
  researchGroupMap: Map<string, KeyValueType[]>;
  synthesisInstrumentOptions: KeyValueType[];
  characterizationInstrumentOptions: KeyValueType[];
  sampleMaterialOptions: MaterialDto[];
  columnType: KeyValueType[];
  metadataSource: KeyValueType[];
  instrumentFunding: InstrumentFundingDto[];
  projectCategory: KeyValueType[];
  projectSearchStatus: KeyValueType[];
  projectType: ProjectTypeDto[];
  publicationType: KeyValueType[];
  projectStatus: KeyValueType[];
  sampleReportingCategory: ReportingCategoryDto[];
  status: KeyValueType[];
  visibility: KeyValueType[];
  activityCategory: KeyValueType[];
  sampleRequestStatus: KeyValueType[];
  activityType: KeyValueType[];
  reportObject: KeyValueType[];
  reportAggregation: KeyValueType[];
  reportAttribute: KeyValueType[];
  shippingLabelTypes: string[];
  shippingTypes: string[];
};
